import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icons from "../icons";

/* The Log — who did what to an Artifact or a run, when, on which version of
   the Test, and with what outcome (GET /api/activity, read-only). Opened from
   an Artifact (by testcasekey, with a "This Test" switch to everything of its
   Xray Test), a trial execution (executionkey) or a For you test run
   (xray_test_run_id). */

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";
const PAGE = 50;

/* Action → short label and icon. */
export const ACTIONS = {
  "artifact.generate_requested": { label: "Generate requested", Icon: Icons.Spark },
  "artifact.created": { label: "Artifact created", Icon: Icons.Plus },
  "artifact.recreated": { label: "Artifact re-created", Icon: Icons.Plus },
  "artifact.declined": { label: "Artifact declined", Icon: Icons.X },
  "artifact.edited": { label: "Artifact edited", Icon: Icons.Pencil },
  "artifact.deleted": { label: "Artifact deleted", Icon: Icons.Trash },
  "artifact.outdated_seen": { label: "Found outdated", Icon: Icons.Alert },
  "trial.created": { label: "Trial created", Icon: Icons.Plus },
  "trial.started": { label: "Trial started", Icon: Icons.Play },
  "trial.finished": { label: "Trial finished", Icon: Icons.Check },
  "trial.cancelled": { label: "Trial stopped", Icon: Icons.Pause },
  "trial.deleted": { label: "Trial deleted", Icon: Icons.Trash },
  "run.refused": { label: "Gate check", Icon: Icons.Lock },
  "bug.raised": { label: "Bug raised", Icon: Icons.JiraBug },
  "formal.started": { label: "Formal run started", Icon: Icons.Play },
  "formal.finished": { label: "Formal run finished", Icon: Icons.Check },
  "formal.submitted": { label: "Submitted to Jira", Icon: Icons.BadgeCheck },
  "formal.submit_failed": { label: "Submit failed", Icon: Icons.Alert },
  "formal.execution_finished": { label: "Execution finished", Icon: Icons.ShieldCheck },
};

export const GROUPS = [
  { id: "", label: "All entries" },
  { id: "artifact", label: "Artifact" },
  { id: "trial", label: "Trial" },
  { id: "formal", label: "Formal" },
  { id: "bugs", label: "Bugs" },
];

/* One date-time format everywhere in the panel, in the viewer's local time. */
export function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

const LABELS = {
  stored_hash: "Stored steps hash",
  live_hash: "Live steps hash",
  previous_steps_hash: "Previous steps hash",
  new_steps_hash: "New steps hash",
  previous_version: "Previous version",
  previous_last_run_status: "Previous last run",
  gate_warnings: "Warnings shown",
  preconditions_confirmed: "Preconditions confirmed",
  runkey: "Run",
  inboxkey: "Generation run",
  test_keys: "Tests",
  source_mode: "Source",
  run_id: "Test run",
  test_execution: "Test Execution",
  jira_start: "Started in Jira",
  bug_key: "Bug",
  failure_cause: "Failure Cause",
  failing_step: "Failing step",
  failing_step_name: "Failing step name",
  failing_error: "Error",
  backfilled: "Reconstructed from earlier records",
  stopped_by: "Stopped by",
  last_status: "Last status",
  steps_before: "Steps before",
  steps_after: "Steps after",
};

function labelOf(key) {
  if (LABELS[key]) return LABELS[key];
  const s = String(key).replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* A detail value as text, never raw JSON. */
export function valueText(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) {
    if (!v.length) return "—";
    return v
      .map((x) => (x && typeof x === "object" && "name" in x ? `${x.name}${"value" in x ? ` = ${x.value || "—"}` : ""}` : valueText(x)))
      .join(", ");
  }
  if (typeof v === "object") {
    return Object.entries(v).map(([k, x]) => `${labelOf(k)}: ${valueText(x)}`).join("; ");
  }
  return String(v);
}

function ModeChip({ mode }) {
  if (!mode) return null;
  const formal = mode === "formal";
  return (
    <span
      className={`inline-flex items-center rounded-full px-1.5 py-px text-[0.6875rem] font-medium capitalize ring-1 ring-inset ${
        formal ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20" : "bg-slate-100 text-slate-600 ring-slate-500/20"
      }`}
      title={formal ? "Formal run: For you › Test Runs (evidence)" : "Trial run: not recorded as evidence"}
    >
      {formal ? "formal" : "trial"}
    </span>
  );
}

/* Refused and error entries carry an icon and a word, not colour alone. */
function OutcomeMark({ outcome }) {
  if (!outcome || outcome === "ok") return null;
  const refused = outcome === "refused";
  return (
    <span className={`inline-flex items-center gap-1 text-[0.6875rem] font-semibold ${refused ? "text-amber-800" : "text-rose-700"}`}>
      {refused ? <Icons.Lock className="h-3 w-3" aria-hidden="true" /> : <Icons.Alert className="h-3 w-3" aria-hidden="true" />}
      {refused ? "Refused" : "Error"}
    </span>
  );
}

/* The facts an entry shows above its details (the Jira/Xray ones by default). */
const xrayFacts = (e) => [
  ["Test", e.xray_test_key],
  ["Test status", e.test_status],
  ["Artifact version", e.artifact_version],
  ["Test Execution", e.xray_test_execution_key],
];

/* What kind of thing happened, for the timeline node's colour (always with
   an icon and a label, never colour alone). */
const TONES = {
  create: "bg-emerald-50 text-emerald-700 ring-emerald-600/25 dark:bg-emerald-500/15 dark:text-emerald-300",
  change: "bg-brand-500/10 text-brand-700 ring-brand-600/25 dark:text-brand-300",
  remove: "bg-rose-50 text-rose-700 ring-rose-600/25 dark:bg-rose-500/15 dark:text-rose-300",
  run: "bg-sky-50 text-sky-700 ring-sky-600/25 dark:bg-sky-500/15 dark:text-sky-300",
  warn: "bg-amber-50 text-amber-800 ring-amber-600/25 dark:bg-amber-500/15 dark:text-amber-300",
  neutral: "bg-slate-100 text-slate-600 ring-slate-500/20",
};

const PASSED = /^(passed|pass|success|succeeded|ok|done|completed)$/i;
const FAILED = /^(failed|fail|error|blocked|interrupted|aborted)$/i;

export function toneOf(e) {
  if (e.outcome === "refused") return "warn";
  if (e.outcome === "error") return "remove";
  const verb = String(e.action || "").split(".").pop();
  if (/finished|submitted/.test(verb)) {
    if (PASSED.test(e.status || "")) return "create";
    if (FAILED.test(e.status || "")) return "remove";
    return "run";
  }
  if (/^(created|restored|raised)$/.test(verb)) return "create";
  if (/deleted|retired|declined|cancelled/.test(verb)) return "remove";
  if (/started|requested/.test(verb)) return "run";
  if (/refused|failed|outdated/.test(verb)) return "warn";
  if (/recreated|edited|changed|fixed|updated/.test(verb)) return "change";
  return "neutral";
}

/* A finished run's status, as a word on a chip. */
function StatusChip({ e }) {
  const verb = String(e.action || "").split(".").pop();
  if (!e.status || !/finished/.test(verb)) return null;
  const cls = PASSED.test(e.status) ? TONES.create : FAILED.test(e.status) ? TONES.remove : TONES.neutral;
  return (
    <span className={`inline-flex items-center rounded-full px-1.5 py-px text-[0.6875rem] font-medium capitalize ring-1 ring-inset ${cls}`}>
      {e.status}
    </span>
  );
}

/* "5 min ago", for entries of the last week; nothing after that. */
export function relativeTime(iso, now = Date.now()) {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.round((now - t) / 1000);
  if (s < 0) return "";
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} d ago`;
  return "";
}

/* The day an entry belongs to: "Today", "Yesterday" or the date. */
export function dayLabel(iso, now = new Date()) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}

function initials(name) {
  const parts = String(name || "?").trim().split(/[\s._@-]+/).filter(Boolean);
  return ((parts[0]?.[0] || "?") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

const defaultDetails = (e) => Object.entries(e.details || {});

/* One person's initials on a colour picked from their name, so the same
   person reads the same everywhere in the panel. */
const AVATARS = [
  "bg-brand-500/15 text-brand-700 dark:text-brand-300",
  "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200",
  "bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-200",
  "bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200",
  "bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200",
  "bg-slate-200 text-slate-700",
];
function Avatar({ name, size = "h-6 w-6 text-[0.625rem]", className = "" }) {
  const n = String(name || "");
  let h = 0;
  for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) >>> 0;
  return (
    <span aria-hidden="true" className={`flex flex-none items-center justify-center rounded-full font-bold ${size} ${AVATARS[h % AVATARS.length]} ${className}`}>
      {initials(name)}
    </span>
  );
}

function dayKey(iso) {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return Number.isNaN(d.getTime()) ? "" : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function duration(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${s % 60} s`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

/* A finished entry's start: the latest earlier "started" entry of the same
   execution or run among those loaded. */
const RUN_KEYS = ["execution_key", "executionkey", "runkey", "run_id", "xray_test_run_id"];
function startedFor(e, items) {
  if (!/finished$/.test(String(e.action))) return null;
  const k = RUN_KEYS.find((x) => e[x]);
  if (!k) return null;
  const t = new Date(e.occurred_at).getTime();
  return (
    items.find(
      (o) => o !== e && o[k] === e[k] && /started$/.test(String(o.action)) && new Date(o.occurred_at).getTime() <= t,
    ) || null
  );
}

/* Entries (newest first) under a heading per day. */
function byDay(items) {
  const days = [];
  for (const e of items) {
    const label = dayLabel(e.occurred_at);
    if (!days.length || days[days.length - 1].label !== label) days.push({ label, items: [] });
    days[days.length - 1].items.push(e);
  }
  return days;
}

/* What the search box looks through. */
function searchText(e, actions, factsOf, summaryOf) {
  const meta = actions[e.action];
  return [meta?.label, e.action, e.message, summaryOf ? summaryOf(e) : "", e.user_name, e.status, ...factsOf(e).map(([, v]) => v)]
    .filter((x) => x !== null && x !== undefined)
    .join(" ")
    .toLowerCase();
}

/* The search's words, marked where they appear. */
function Marked({ text, needle }) {
  const s = String(text ?? "");
  if (!needle) return s;
  const at = s.toLowerCase().indexOf(needle);
  if (at < 0) return s;
  return (
    <>
      {s.slice(0, at)}
      <mark className="rounded-sm bg-amber-200/70 px-px text-inherit dark:bg-amber-400/30">{s.slice(at, at + needle.length)}</mark>
      {s.slice(at + needle.length)}
    </>
  );
}

/* The loaded entries as CSV, for a reviewer's own records (secrets are
   already masked by the API). */
export function toCsv(items, actions, factsOf, summaryOf) {
  const factNames = [];
  for (const e of items) for (const [k, v] of factsOf(e)) if (v !== null && v !== undefined && v !== "" && !factNames.includes(k)) factNames.push(k);
  const head = ["When (UTC)", "Who", "What happened", "Action code", "Outcome", "Status", "Summary", ...factNames, "Record id"];
  const cell = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = items.map((e) => {
    const facts = Object.fromEntries(factsOf(e));
    return [
      e.occurred_at,
      e.user_name || "",
      actions[e.action]?.label || e.action,
      e.action,
      e.outcome || "ok",
      e.status || "",
      summaryOf ? summaryOf(e) : e.message,
      ...factNames.map((k) => facts[k]),
      e.activitykey,
    ];
  });
  return [head, ...rows].map((r) => r.map(cell).join(",")).join("\r\n");
}

function download(name, text, type) {
  try {
    const url = URL.createObjectURL(new Blob([text], { type }));
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch {
    /* No download in this browser (or a test): nothing to do. */
  }
}

/* The overview: how many entries, by whom, since when, and the people (one
   click shows only theirs). */
function Overview({ items, people, more, person, onPerson }) {
  const stats = useMemo(() => {
    const who = new Map();
    const days = new Set();
    for (const e of items) {
      days.add(dayKey(e.occurred_at));
      if (e.user_name) who.set(e.user_name, (who.get(e.user_name) || 0) + 1);
    }
    return { contributors: [...who.entries()].sort((a, b) => b[1] - a[1]), active: days.size };
  }, [items]);

  if (!items.length) return null;
  const newest = items[0];
  const oldest = items[items.length - 1];
  const n = items.length;
  const names = stats.contributors.length;
  const ago = relativeTime(newest.occurred_at);
  const since = new Date(oldest.occurred_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  const oneDay = stats.active === 1 ? dayLabel(oldest.occurred_at) : "";
  const when = oneDay === "Today" ? "all today" : oneDay === "Yesterday" ? "all yesterday" : oneDay ? `all on ${since}` : `since ${since}`;

  return (
    <section aria-label="Overview" className="flex-none border-b border-slate-200 px-5 py-3.5">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
        <div className="flex min-w-[14rem] flex-1 flex-wrap items-center justify-between gap-x-6 gap-y-2">
          <div>
          <p className="text-[0.9375rem] leading-snug text-slate-800">
            <span className="font-semibold tabular-nums">{more ? `The latest ${n}` : n}</span> {n === 1 ? "entry" : "entries"} by{" "}
            <span className="font-semibold">{names === 1 ? stats.contributors[0][0] : `${names} people`}</span>
            , {when}.
          </p>
          <p className="mt-0.5 text-[0.75rem] text-slate-500">
            Last change {ago || formatWhen(newest.occurred_at)}, by {newest.user_name || "an unknown user"}.
          </p>
          </div>
          {stats.contributors.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex -space-x-1.5">
                {stats.contributors.slice(0, 6).map(([name, count]) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => onPerson(person === name ? "" : name)}
                    aria-pressed={person === name}
                    title={`${name}: ${count} ${count === 1 ? "entry" : "entries"}${person === name ? " (showing only these)" : ""}`}
                    aria-label={`Only ${name}'s entries`}
                    className={`rounded-full ring-2 transition hover:z-10 hover:-translate-y-0.5 ${person === name ? "z-10 ring-brand-500" : "ring-card"}`}
                  >
                    <Avatar name={name} size="h-7 w-7 text-[0.625rem]" />
                  </button>
                ))}
              </div>
              {stats.contributors.length > 6 && <span className="text-[0.75rem] text-slate-500">and {stats.contributors.length - 6} more</span>}
              {people.length > 0 && person && (
                <button type="button" onClick={() => onPerson("")} className="text-[0.75rem] font-medium text-brand-700 hover:underline">
                  Everyone
                </button>
              )}
            </div>
          )}
        </div>

      </div>
    </section>
  );
}

/* What an entry is about, for telling repeats apart. */
const TARGETS = ["test_key", "artifact_key", "execution_key", "suitekey", "runkey", "testcasekey", "executionkey", "xray_test_run_id", "xray_test_key"];
const RUN_GAP = 15 * 60 * 1000;

/* Consecutive entries (newest first) of the same action, by the same person,
   on the same thing, each within RUN_GAP of the one before: one run. */
export function runsOf(items) {
  const runs = [];
  const sig = (e) => `${e.action}|${e.user_name || ""}|${TARGETS.map((k) => e[k] || "").join("|")}|${e.outcome || ""}`;
  for (const e of items) {
    const last = runs[runs.length - 1];
    const prev = last && (last.rest[last.rest.length - 1] || last.head);
    if (last && sig(prev) === sig(e) && Math.abs(new Date(prev.occurred_at) - new Date(e.occurred_at)) <= RUN_GAP) last.rest.push(e);
    else runs.push({ head: e, rest: [] });
  }
  return runs;
}

/* One row of the ledger: when, what, who, and the sentence, in one glance. */
/* A title whose last word says what happened ("created", "recreated"):
   that word in the colour `kind.wordCls` gives it, when the title ends with
   `kind.word`. */
function TitleText({ text, kind, needle = "" }) {
  const word = kind?.word;
  const m = word ? new RegExp(`^(.*\\s)(${word})([.]?)$`, "i").exec(text || "") : null;
  if (!m) return <Marked text={text} needle={needle} />;
  return (
    <>
      <Marked text={m[1]} needle={needle} />
      <span className={`font-medium ${kind.wordCls || "text-slate-500"}`}>
        <Marked text={m[2]} needle={needle} />
      </span>
      {m[3]}
    </>
  );
}

function Row({ e, meta, selected, onSelect, needle, summary, hint, rowRef, repeat, nested = false, title, kind }) {
  const time = new Date(e.occurred_at);
  const short = Number.isNaN(time.getTime()) ? "" : time.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const tone = toneOf(e);
  const Icon = kind?.Icon || meta.Icon;
  return (
    <li className="relative">
      <button
        ref={rowRef}
        type="button"
        onClick={onSelect}
        aria-current={selected ? "true" : undefined}
        className={`group flex w-full items-start gap-3 border-l-[0.1875rem] py-2.5 pr-4 text-left transition focus:outline-none focus-visible:bg-slate-100 ${nested ? "pl-10" : "pl-4"} ${
          selected
            ? `${kind?.bar || "border-brand-600"} bg-brand-500/10 ${kind ? "ring-1 ring-inset ring-brand-500/30" : ""}`
            : `${kind?.bar || "border-transparent"} hover:bg-slate-100/70`
        }`}
      >
        <span
          aria-hidden="true"
          title={kind ? meta.label : undefined}
          className={`mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-lg ring-1 ring-inset ${kind?.cls || TONES[tone]}`}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            {kind && <span className="sr-only">{meta.label}: </span>}
            <span
              className={`min-w-0 text-[0.8125rem] font-semibold ${title ? "line-clamp-2" : "truncate"} ${selected ? "text-brand-700 dark:text-brand-300" : "text-slate-900"}`}
              title={title || undefined}
            >
              {title ? <TitleText text={title} kind={kind} needle={needle} /> : <Marked text={meta.label} needle={needle} />}
            </span>
            <span className="flex flex-none items-center gap-1.5">
              <ModeChip mode={e.run_mode} />
              <StatusChip e={e} />
              <OutcomeMark outcome={e.outcome} />
            </span>
            <time dateTime={e.occurred_at} title={formatWhen(e.occurred_at)} className="ml-auto flex-none text-[0.6875rem] tabular-nums text-slate-500">
              <span aria-hidden="true">{short}</span>
              <span className="sr-only">{formatWhen(e.occurred_at)}</span>
            </time>
          </span>
          <span className="mt-0.5 flex items-center gap-1.5 text-[0.75rem] text-slate-500">
            <Avatar name={e.user_name} size="h-4 w-4 text-[0.5rem]" />
            <span className="truncate text-slate-700">
              <Marked text={e.user_name || "Unknown user"} needle={needle} />
            </span>
            {hint && <span className="ml-auto flex-none">{hint}</span>}
          </span>
          {summary && !title && (
            <span className="mt-1 line-clamp-2 block text-[0.75rem] leading-snug text-slate-600" title={summary}>
              <Marked text={summary} needle={needle} />
            </span>
          )}
        </span>
      </button>
      {repeat && (
        <button
          type="button"
          onClick={repeat.onToggle}
          aria-expanded={repeat.open}
          className="mb-2 ml-[3.75rem] inline-flex items-center gap-1 rounded-full bg-slate-200/70 px-2 py-0.5 text-[0.6875rem] font-medium text-slate-700 hover:bg-slate-200"
        >
          {repeat.open ? <Icons.ChevronUp className="h-3 w-3" /> : <Icons.ChevronDown className="h-3 w-3" />}
          {repeat.open ? "Hide the earlier ones" : `${repeat.count} more like this, earlier`}
        </button>
      )}
    </li>
  );
}

function Section({ title, children, aside }) {
  return (
    <section className="border-t border-slate-100 px-6 py-4">
      <div className="mb-2.5 flex items-center gap-2">
        <h4 className="text-[0.8125rem] font-semibold text-slate-900">{title}</h4>
        {aside && <span className="ml-auto">{aside}</span>}
      </div>
      {children}
    </section>
  );
}

/* The selected entry in full: what happened, who and when (local and UTC,
   how long a run took), what changed, the record's facts and details, and the
   audit record itself. */
function Inspector({ e, items, actions, factsOf, renderExtra, renderInline, detailsOf, summaryOf, titleOf, kindOf, auditRecord = true, onBack }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => setCopied(false), [e]);
  const meta = actions[e.action] || { label: e.action, Icon: Icons.Log };
  const tone = toneOf(e);
  const kind = kindOf ? kindOf(e) : null;
  const Icon = kind?.Icon || meta.Icon;
  const facts = factsOf(e).filter(([, v]) => v !== null && v !== undefined && v !== "");
  const details = detailsOf(e);
  const inline = renderInline ? renderInline(e, { items }) : null;
  const extra = renderExtra ? renderExtra(e, { items }) : null;
  const summary = summaryOf ? summaryOf(e) : e.message;
  /* With a one-line title (ID first) it is the heading, and who and when
     sit beside it in place of the card below. */
  const title = titleOf ? titleOf(e) : "";
  const started = startedFor(e, items);
  const took = started ? duration(new Date(e.occurred_at) - new Date(started.occurred_at)) : "";
  const when = new Date(e.occurred_at);
  const utc = Number.isNaN(when.getTime()) ? "" : `${when.toISOString().replace("T", " ").slice(0, 19)} UTC`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(e, null, 2));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <article aria-label="Selected entry" className="min-h-0">
      <header className="px-6 pb-4 pt-5">
        {onBack && (
          <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-[0.75rem] font-medium text-brand-700 hover:underline">
            <Icons.ChevronLeft className="h-3.5 w-3.5" /> All entries
          </button>
        )}
        <div className="flex items-start gap-3.5">
          <span aria-hidden="true" className={`flex h-11 w-11 flex-none items-center justify-center rounded-xl ring-1 ring-inset ${kind?.cls || TONES[tone]}`}>
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-[1.125rem] font-semibold leading-tight tracking-tight text-slate-900">
                {title ? (
                  <>
                    <span className="sr-only">{meta.label}: </span>
                    <TitleText text={title} kind={kind} />
                  </>
                ) : (
                  meta.label
                )}
              </h3>
              <ModeChip mode={e.run_mode} />
              <StatusChip e={e} />
              <OutcomeMark outcome={e.outcome} />
            </div>
            {!title && summary && <p className="mt-1.5 max-w-[68ch] text-[0.875rem] leading-relaxed text-slate-700">{summary}</p>}
          </div>
          {title && (
            <dl aria-label="Who and when" className="flex flex-none items-center gap-2.5 text-right">
              <div className="min-w-0">
                <dt className="sr-only">Done by</dt>
                <dd className="max-w-[14rem] truncate text-[0.8125rem] font-semibold text-slate-900">{e.user_name || "Unknown user"}</dd>
                <dt className="sr-only">When</dt>
                <dd className="text-[0.75rem] tabular-nums text-slate-600" title={utc}>
                  {formatWhen(e.occurred_at)}
                </dd>
                <dd className="text-[0.6875rem] tabular-nums text-slate-400">{took ? `Took ${took}` : relativeTime(e.occurred_at)}</dd>
              </div>
              <Avatar name={e.user_name} size="h-9 w-9 text-[0.6875rem]" />
            </dl>
          )}
        </div>
        {!title && (
        <dl className="mt-4 grid grid-cols-1 gap-3 rounded-xl bg-slate-50 px-4 py-3 sm:grid-cols-3">
          <div className="flex items-center gap-2.5">
            <Avatar name={e.user_name} size="h-8 w-8 text-[0.6875rem]" />
            <div className="min-w-0">
              <dt className="text-[0.6875rem] text-slate-500">Done by</dt>
              <dd className="truncate text-[0.8125rem] font-semibold text-slate-900">{e.user_name || "Unknown user"}</dd>
            </div>
          </div>
          <div className="min-w-0">
            <dt className="text-[0.6875rem] text-slate-500">When</dt>
            <dd className="text-[0.8125rem] font-medium tabular-nums text-slate-900">{formatWhen(e.occurred_at)}</dd>
            <dd className="text-[0.6875rem] tabular-nums text-slate-500">{[relativeTime(e.occurred_at), utc].filter(Boolean).join(", ")}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[0.6875rem] text-slate-500">{took ? "Took" : "Outcome"}</dt>
            <dd className="text-[0.8125rem] font-medium text-slate-900">
              {took || (e.outcome === "refused" ? "Refused" : e.outcome === "error" ? "Error" : "Done")}
            </dd>
            {took && <dd className="text-[0.6875rem] tabular-nums text-slate-500">Started {formatWhen(started.occurred_at)}</dd>}
          </div>
        </dl>
        )}
      </header>

      {(inline || extra) && (
        <Section title="What changed">
          <div className="space-y-3">
            {inline}
            {extra}
          </div>
        </Section>
      )}

      {(facts.length > 0 || details.length > 0) && title && (
        <Section title="Record">
          <dl className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 text-[0.8125rem]">
            {[...facts, ...details.map(([k, v]) => [labelOf(k), v])].map(([k, v]) => (
              <div key={k} className="grid grid-cols-[9rem_1fr] gap-x-4 px-3 py-2 sm:grid-cols-[11rem_1fr]">
                <dt className="truncate text-[0.75rem] font-medium text-slate-500">{k}</dt>
                <dd className="min-w-0 break-words font-medium text-slate-900">{valueText(v)}</dd>
              </div>
            ))}
          </dl>
        </Section>
      )}

      {(facts.length > 0 || details.length > 0) && !title && (
        <Section title="Record">
          <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-6 gap-y-1.5 text-[0.8125rem]">
            {[...facts, ...details.map(([k, v]) => [labelOf(k), v])].map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-slate-500">{k}</dt>
                <dd className="min-w-0 break-words font-medium text-slate-800">{valueText(v)}</dd>
              </React.Fragment>
            ))}
          </dl>
        </Section>
      )}

      {auditRecord && (
      <Section
        title="Audit record"
        aside={
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[0.75rem] font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          >
            {copied ? <Icons.Check className="h-3.5 w-3.5" /> : <Icons.Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy as JSON"}
          </button>
        }
      >
        <p className="text-[0.75rem] leading-relaxed text-slate-500">
          Kept as written and never changed or deleted. Record <span className="font-mono text-slate-700">{e.activitykey}</span>, action{" "}
          <span className="font-mono text-slate-700">{e.action}</span>.
        </p>
      </Section>
      )}
    </article>
  );
}

/* The panel. `scope` is exactly one of {testcasekey, executionkey,
   xray_test_run_id, xray_test_key}; with `testKey` beside a testcasekey, a
   switch shows everything of that Test instead.
   App Pages read their own log through the same panel: `endpoint`
   ("app-pages/activity"), their `groups` and `actions`, `testScope` (what
   "This Test" shows), `factsOf` (an entry's facts), `summaryOf` (its
   sentence, its message by default), `hintOf` (a few words on its row),
   `renderInline` and `renderExtra` (what changed, given the loaded entries
   to compare versions) and `detailsOf` (its detail fields).
   An overview (sentence, contributors, entries per day) sits above a ledger
   of entries grouped by day; the selected one shows in full beside it (in
   its place when the dialog is narrow).
   App Pages trim it: `heading` (the dialog's name), `overview` and
   `dayHeadings` off, `multiPerson` (people as checkboxes, none = everyone),
   `sinceDays` (a From / To date filter, the last N days by default),
   `titleOf` (a row's one line, in place of its label and sentence) and
   `kindOf` ({Icon, cls, bar, word, wordCls}: the icon of what happened, the
   colours of what the entry is about (the icon's and the row's left line),
   and the title's last word in its own colour), `combine` (the loaded
   entries as the list shows them, e.g. two entries of one change as one;
   the CSV keeps them all), `defaultGroup` (the filter it opens on).
   A group may carry an `Icon` for its chip. `closeOnlyWithX`: only the X
   closes it (no Esc, no click outside, no keys hint). `auditRecord` off
   leaves out the entry's id / action code / Copy as JSON section. */
export default function ActivityPanel({
  title,
  scope,
  testKey = "",
  onClose,
  endpoint = "activity",
  groups = GROUPS,
  actions = ACTIONS,
  testScope,
  factsOf = xrayFacts,
  renderExtra,
  renderInline,
  detailsOf = defaultDetails,
  summaryOf,
  hintOf,
  startMaximized = false,
  heading = "Log",
  overview = true,
  dayHeadings = true,
  multiPerson = false,
  sinceDays = 0,
  titleOf,
  kindOf,
  combine,
  defaultGroup = "",
  closeOnlyWithX = false,
  auditRecord = true,
}) {
  const [wholeTest, setWholeTest] = useState(false);
  const [group, setGroup] = useState(defaultGroup);
  const [person, setPerson] = useState("");
  const [persons, setPersons] = useState([]);
  const [range, setRange] = useState(() => defaultRange(sinceDays));
  const [search, setSearch] = useState("");
  const day = "";
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);
  const [openRuns, setOpenRuns] = useState({});
  const closeRef = useRef(null);
  const searchRef = useRef(null);
  const rowRefs = useRef({});

  const wholeScope = testScope || (scope?.testcasekey ? { xray_test_key: testKey } : null);
  const query = wholeTest && testKey && wholeScope ? wholeScope : scope;

  const load = useCallback(
    async (before = null) => {
      setLoading(true);
      setError("");
      const params = new URLSearchParams({ limit: String(PAGE) });
      for (const [k, v] of Object.entries(query || {})) if (v) params.set(k, String(v));
      const kind = groups.find((g) => g.id === group);
      if (kind?.actions) params.set("action", kind.actions.join(","));
      else if (group) params.set("group", group);
      const who = multiPerson ? persons.join(",") : person;
      if (who) params.set("person", who);
      if (sinceDays) {
        const since = dayStart(range.from);
        const until = dayEnd(range.to);
        if (since) params.set("since", since);
        if (until) params.set("until", until);
      }
      if (before) params.set("before", before);
      try {
        const res = await fetch(`${API_BASE_URL}${endpoint}?${params.toString()}`);
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(typeof body?.detail === "string" ? body.detail : `HTTP ${res.status}`);
        const data = body?.data || {};
        setItems((old) => (before ? [...old, ...(data.items || [])] : data.items || []));
        setPeople(data.people || []);
        setNext(data.next_before || null);
      } catch (e) {
        setError(e.message || "The log could not be loaded.");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, JSON.stringify(query), group, person, groups, multiPerson, persons.join(","), sinceDays, range.from, range.to],
  );

  useEffect(() => {
    load(null);
  }, [load]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !closeOnlyWithX) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, closeOnlyWithX]);

  const frame = useResizable(startMaximized);
  const width = frame.maxed ? (window.innerWidth || 1280) - GUTTER : frame.size.w;
  const wide = width >= 760;

  const needle = search.trim().toLowerCase();
  const shown = useMemo(
    () =>
      (combine ? combine(items) : items).filter(
        (e) => (!day || dayKey(e.occurred_at) === day) && (!needle || searchText(e, actions, factsOf, summaryOf).includes(needle)),
      ),
    [items, day, needle, actions, factsOf, summaryOf, combine],
  );
  const selected = shown.find((e) => e.activitykey === selectedKey) || (wide ? shown[0] : null) || null;
  const days = useMemo(
    () => (dayHeadings ? byDay(shown) : [{ label: "", items: shown }]).map((d) => ({ ...d, runs: runsOf(d.items) })),
    [shown, dayHeadings],
  );
  const visible = days.flatMap((d) => d.runs.flatMap((r) => (openRuns[r.head.activitykey] ? [r.head, ...r.rest] : [r.head])));

  const move = (by) => {
    if (!visible.length) return;
    const at = selected ? visible.indexOf(selected) : -1;
    const nextItem = visible[Math.min(visible.length - 1, Math.max(0, at + by))];
    setSelectedKey(nextItem.activitykey);
    rowRefs.current[nextItem.activitykey]?.focus();
  };
  const onListKey = (ev) => {
    if (ev.key === "ArrowDown" || ev.key === "j") {
      ev.preventDefault();
      move(1);
    } else if (ev.key === "ArrowUp" || ev.key === "k") {
      ev.preventDefault();
      move(-1);
    }
  };
  const onDialogKey = (ev) => {
    const typing = /^(INPUT|SELECT|TEXTAREA)$/.test(ev.target.tagName);
    if (ev.key === "/" && !typing) {
      ev.preventDefault();
      searchRef.current?.focus();
    }
  };
  const exportCsv = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const slug = String(title || "log").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
    download(`log-${slug}-${stamp}.csv`, toCsv(shown, actions, factsOf, summaryOf), "text/csv;charset=utf-8");
  };

  const inputCls =
    "h-8 rounded-lg border border-slate-200 bg-card px-2 text-[0.75rem] text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500";
  const chipCls = (on) =>
    `rounded-full px-3 py-1 text-[0.75rem] font-medium transition ${
      on ? "bg-slate-900 text-white shadow-sm dark:bg-brand-600" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
    }`;
  const showList = wide || !selected;

  const inspector = selected && (
    <Inspector
      e={selected}
      items={items}
      actions={actions}
      factsOf={factsOf}
      renderExtra={renderExtra}
      renderInline={renderInline}
      detailsOf={detailsOf}
      summaryOf={summaryOf}
      titleOf={titleOf}
      kindOf={kindOf}
      auditRecord={auditRecord}
      onBack={wide ? null : () => setSelectedKey(null)}
    />
  );

  return (
    /* Opened from inside a clickable card: its clicks stay here. */
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
      {closeOnlyWithX ? (
        <div aria-hidden="true" className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
      ) : (
        <button type="button" aria-label="Close the log" onClick={onClose} className="absolute inset-0 cursor-default bg-slate-900/40 backdrop-blur-sm" />
      )}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Log: ${title}`}
        onKeyDown={onDialogKey}
        style={frame.style}
        className="relative z-10 flex max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl bg-card shadow-2xl ring-1 ring-slate-200"
      >
        <div className="flex flex-none items-center gap-3 border-b border-slate-200 px-5 py-3">
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-brand-500/10 text-brand-700">
            <Icons.Log className="h-[1.125rem] w-[1.125rem]" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-[1rem] font-semibold tracking-tight text-slate-900">{heading}</h2>
            <p className="truncate text-[0.75rem] text-slate-500" title={title}>{title}</p>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            disabled={!shown.length}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-card px-2.5 text-[0.75rem] font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-40"
          >
            <Icons.Download className="h-3.5 w-3.5" /> Export CSV
          </button>
          <button
            type="button"
            onClick={frame.toggleMax}
            aria-pressed={frame.maxed}
            aria-label={frame.maxed ? "Restore size" : "Maximize"}
            title={frame.maxed ? "Restore size" : "Maximize"}
            className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <Icons.Maximize className="h-4 w-4" />
          </button>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close the log"
            className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        {overview && (
          <Overview
            items={items}
            people={people}
            more={!!next}
            person={person}
            onPerson={setPerson}
          />
        )}

        <div className="flex flex-none flex-wrap items-center gap-2 border-b border-slate-200 px-5 py-2.5">
          <div role="group" aria-label="Kind of entry" className="flex flex-wrap gap-0.5">
            {groups.map((g) => (
              <button
                key={g.id}
                type="button"
                aria-pressed={group === g.id}
                onClick={() => setGroup(g.id)}
                className={`${chipCls(group === g.id)} ${g.Icon ? "inline-flex items-center gap-1.5" : ""}`}
              >
                {g.Icon && <g.Icon aria-hidden="true" className="h-3.5 w-3.5 flex-none" />}
                {g.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {testKey && wholeScope && (
              <div role="group" aria-label="Show" className="inline-flex overflow-hidden rounded-lg ring-1 ring-slate-200">
                {[
                  [false, "This Artifact"],
                  [true, `This Test (${testKey})`],
                ].map(([on, label]) => (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={wholeTest === on}
                    onClick={() => setWholeTest(on)}
                    className={`px-2.5 py-1 text-[0.75rem] font-medium ${wholeTest === on ? "bg-brand-600 text-white" : "bg-card text-slate-600 hover:bg-slate-50"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {sinceDays > 0 && <DateRange range={range} onChange={setRange} inputCls={inputCls} />}
            {multiPerson ? (
              <PeoplePicker people={people} chosen={persons} onChange={setPersons} inputCls={inputCls} />
            ) : (
              <>
                <label className="sr-only" htmlFor="activity-person">Person</label>
                <select id="activity-person" value={person} onChange={(e) => setPerson(e.target.value)} className={inputCls}>
                  <option value="">Everyone</option>
                  {people.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </>
            )}
            <div className="relative w-56">
              <Icons.Search aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchRef}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search the log"
                placeholder="Search a key, a person, a value"
                className={`${inputCls} w-full pl-8 pr-7`}
              />
              <kbd aria-hidden="true" className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-slate-200 px-1 text-[0.625rem] text-slate-400">
                /
              </kbd>
            </div>
          </div>
          {(day || needle) && (
            <p className="flex w-full flex-wrap items-center gap-2 text-[0.75rem] text-slate-600" aria-live="polite">
              Showing {shown.length} of {items.length}
              {needle && <> matching “{search.trim()}”</>}.
              <button
                type="button"
                onClick={() => setSearch("")}
                className="font-medium text-brand-700 hover:underline"
              >
                Show all
              </button>
            </p>
          )}
        </div>

        <div className="flex min-h-0 flex-1">
          {showList && (
            <div
              className={`cq-scroll relative min-h-0 overflow-y-auto bg-slate-50/60 ${wide ? "w-[42%] min-w-[18.75rem] max-w-[27.5rem] flex-none border-r border-slate-200" : "flex-1"}`}
            >
              {error && (
                <p role="alert" className="m-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  <Icons.Alert className="h-3.5 w-3.5 flex-none" /> {error}
                </p>
              )}
              {!error && !loading && items.length === 0 && (
                <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                    <Icons.Clock className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <p className="text-sm font-medium text-slate-600">
                    {sinceDays || persons.length || group ? "Nothing in the log for these filters." : "Nothing in the log yet."}
                  </p>
                  <p className="max-w-[28ch] text-[0.75rem] text-slate-400">
                    {sinceDays ? "Try other dates or users." : "Creating, re-creating, changing values and running show up here."}
                  </p>
                </div>
              )}
              {!loading && items.length > 0 && shown.length === 0 && (
                <p className="px-6 py-16 text-center text-sm text-slate-500">Nothing loaded matches these filters.</p>
              )}
              {shown.length > 0 && (
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                <ul aria-label="Log entries" onKeyDown={onListKey}>
                  {days.map((d) => (
                    <React.Fragment key={d.label || "all"}>
                      {dayHeadings && (
                        <li role="presentation" className="sticky top-0 z-[2] flex items-baseline gap-2 border-b border-slate-200/70 bg-slate-50/95 px-4 py-1.5 backdrop-blur">
                          <h3 className="text-[0.75rem] font-semibold text-slate-800">{d.label}</h3>
                          <span className="text-[0.6875rem] tabular-nums text-slate-400">
                            {d.items.length} {d.items.length === 1 ? "entry" : "entries"}
                          </span>
                        </li>
                      )}
                      {d.runs.map((r) => {
                        const open = !!openRuns[r.head.activitykey];
                        const row = (e, extra = {}) => (
                          <Row
                            key={e.activitykey}
                            e={e}
                            meta={actions[e.action] || { label: e.action, Icon: Icons.Log }}
                            selected={selected === e}
                            onSelect={() => setSelectedKey(e.activitykey)}
                            needle={needle}
                            summary={summaryOf ? summaryOf(e) : e.message}
                            hint={hintOf ? hintOf(e) : null}
                            title={titleOf ? titleOf(e) : undefined}
                            kind={kindOf ? kindOf(e) : null}
                            rowRef={(el) => {
                              rowRefs.current[e.activitykey] = el;
                            }}
                            {...extra}
                          />
                        );
                        return (
                          <React.Fragment key={r.head.activitykey}>
                            {row(r.head, r.rest.length ? {
                              repeat: {
                                count: r.rest.length,
                                open,
                                onToggle: () => setOpenRuns((o) => ({ ...o, [r.head.activitykey]: !open })),
                              },
                            } : {})}
                            {open && r.rest.map((e) => row(e, { nested: true }))}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </ul>
              )}
              {loading && items.length === 0 && (
                <div aria-hidden="true" className="space-y-1 px-4 py-3">
                  {[0, 1, 2, 3].map((k) => (
                    <div key={k} className="flex animate-pulse gap-3 py-2">
                      <div className="h-7 w-7 rounded-lg bg-slate-200" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-3 w-1/2 rounded bg-slate-200" />
                        <div className="h-2.5 w-1/3 rounded bg-slate-100" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {loading && (
                <p className="flex items-center justify-center gap-2 px-6 py-3 text-xs text-slate-400">
                  <Icons.Spinner className="h-4 w-4 animate-spin" /> Loading the log…
                </p>
              )}
              {!loading && next && (
                <div className="flex justify-center px-4 py-3">
                  <button
                    type="button"
                    onClick={() => load(next)}
                    className="rounded-lg border border-slate-200 bg-card px-3 py-1.5 text-[0.75rem] font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                  >
                    Load more
                  </button>
                </div>
              )}
            </div>
          )}
          {(wide || selected) && (
            <div className="cq-scroll relative min-h-0 min-w-0 flex-1 overflow-y-auto">
              {inspector || (
                <div className="flex h-full items-center justify-center px-6 text-center text-[0.8125rem] text-slate-400">
                  {loading ? "" : "Pick an entry to see it in full."}
                </div>
              )}
            </div>
          )}
        </div>
        {wide && !closeOnlyWithX && (
          <p className="flex-none border-t border-slate-200 bg-slate-50/60 px-5 py-1.5 text-[0.6875rem] text-slate-400">
            <kbd className="rounded border border-slate-200 bg-card px-1">↑</kbd> <kbd className="rounded border border-slate-200 bg-card px-1">↓</kbd> move
            between entries, <kbd className="rounded border border-slate-200 bg-card px-1">/</kbd> search,{" "}
            <kbd className="rounded border border-slate-200 bg-card px-1">Esc</kbd> close. Drag a corner to resize.
          </p>
        )}
        {!frame.maxed && <ResizeHandles frame={frame} />}
      </div>
    </div>
  );
}

/* A local date as YYYY-MM-DD, and a From / To range ending today. */
function isoDay(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function lastDays(days, now = new Date()) {
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
  return { preset: String(days), from: isoDay(from), to: isoDay(now) };
}

export function defaultRange(days, now = new Date()) {
  return days ? lastDays(days, now) : { preset: "", from: "", to: "" };
}

/* The date menu: a few ranges ending today, or Custom (From / To). */
const PRESETS = [
  ["0", "Today"],
  ["7", "Last 7 days"],
  ["30", "Last 30 days"],
  ["90", "Last 90 days"],
];

/* The start and end of a local day, as the instants the API compares. */
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const dayStart = (day) => (DAY.test(day || "") ? new Date(`${day}T00:00:00`).toISOString() : "");
const dayEnd = (day) => (DAY.test(day || "") ? new Date(`${day}T23:59:59.999`).toISOString() : "");

function DateRange({ range, onChange, inputCls }) {
  const presets = PRESETS.some(([v]) => v === range.preset) || range.preset === "custom" ? PRESETS : [...PRESETS, [range.preset, `Last ${range.preset} days`]];
  return (
    <div role="group" aria-label="Dates" className="flex items-center gap-1.5">
      <div className="relative">
        <Icons.Calendar aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <label className="sr-only" htmlFor="activity-dates">Dates</label>
        <select
          id="activity-dates"
          value={range.preset}
          onChange={(e) => {
            const v = e.target.value;
            onChange((r) => (v === "custom" ? { ...r, preset: "custom" } : lastDays(Number(v))));
          }}
          className={`${inputCls} pl-8`}
        >
          {presets.map(([v, label]) => (
            <option key={v} value={v}>{label}</option>
          ))}
          <option value="custom">Custom…</option>
        </select>
      </div>
      {range.preset === "custom" && <DateInputs range={range} onChange={onChange} inputCls={inputCls} />}
    </div>
  );
}

function DateInputs({ range, onChange, inputCls }) {
  return (
    <>
      <label className="sr-only" htmlFor="activity-from">From</label>
      <input
        id="activity-from"
        type="date"
        value={range.from}
        max={range.to || undefined}
        onChange={(e) => onChange((r) => ({ ...r, from: e.target.value }))}
        className={inputCls}
      />
      <span aria-hidden="true" className="text-[0.75rem] text-slate-400">–</span>
      <label className="sr-only" htmlFor="activity-to">To</label>
      <input
        id="activity-to"
        type="date"
        value={range.to}
        min={range.from || undefined}
        onChange={(e) => onChange((r) => ({ ...r, to: e.target.value }))}
        className={inputCls}
      />
    </>
  );
}

/* Users as checkboxes in a drop-down; none ticked shows everyone's entries. */
function PeoplePicker({ people, chosen, onChange, inputCls }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (ev) => {
      if (boxRef.current && !boxRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  const toggle = (name) => onChange(chosen.includes(name) ? chosen.filter((n) => n !== name) : [...chosen, name]);
  const label = chosen.length === 0 ? "Users" : chosen.length === 1 ? chosen[0] : `${chosen.length} users`;
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={boxRef}
      className="relative"
      onKeyDown={(ev) => {
        if (ev.key === "Escape" && open) {
          ev.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={chosen.length ? `Users: ${chosen.join(", ")}` : "Users: everyone"}
        onClick={() => setOpen((o) => !o)}
        className={`${inputCls} inline-flex max-w-[12rem] items-center gap-1.5 ${chosen.length ? "border-brand-500 text-brand-700" : ""}`}
      >
        <Icons.Users className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        <span className="truncate">{label}</span>
        <Icons.ChevronDown className="h-3.5 w-3.5 flex-none text-slate-400" aria-hidden="true" />
      </button>
      {open && (
        <div role="group" aria-label="Users" className="absolute right-0 z-30 mt-1 w-60 rounded-lg border border-slate-200 bg-card py-1 shadow-lg">
          {people.length === 0 ? (
            <p className="px-3 py-2 text-[0.75rem] text-slate-400">No users.</p>
          ) : (
            <ul className="cq-scroll max-h-64 overflow-y-auto">
              {people.map((p) => (
                <li key={p}>
                  <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[0.75rem] text-slate-700 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={chosen.includes(p)}
                      onChange={() => toggle(p)}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                    <Avatar name={p} size="h-5 w-5 text-[0.5625rem]" />
                    <span className="truncate">{p}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {chosen.length > 0 && (
            <div className="border-t border-slate-100 px-3 pb-1 pt-1.5">
              <button type="button" onClick={() => onChange([])} className="text-[0.75rem] font-medium text-brand-700 hover:underline">
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* The dialog's size: fixed (so it does not jump as entries load or are
   filtered), at least MIN, resized from its corners (or the grip with the
   arrow keys), remembered in this browser, and maximizable. Its default
   height is the app's dialog height, 85% of the window (still clamped). */
const SIZE_KEY = "cq.activity.size";
const MIN = { w: 600, h: 460 };
const defaultSize = () => ({ w: 1120, h: Math.round((window.innerHeight || 800) * 0.85) });
const GUTTER = 32;

function fit({ w, h }) {
  const maxW = Math.max(320, (window.innerWidth || 1280) - GUTTER);
  const maxH = Math.max(320, (window.innerHeight || 800) - GUTTER);
  return {
    w: Math.round(Math.min(maxW, Math.max(Math.min(MIN.w, maxW), w))),
    h: Math.round(Math.min(maxH, Math.max(Math.min(MIN.h, maxH), h))),
  };
}

function savedSize() {
  try {
    const s = JSON.parse(window.localStorage.getItem(SIZE_KEY) || "null");
    return s && Number.isFinite(s.w) && Number.isFinite(s.h) ? s : null;
  } catch {
    return null;
  }
}

function saveSize(s) {
  try {
    window.localStorage.setItem(SIZE_KEY, JSON.stringify(s));
  } catch {
    /* Private window or blocked storage: the size just isn't remembered. */
  }
}

export function useResizable(startMaximized = false) {
  const [want, setWant] = useState(() => savedSize() || defaultSize());
  const [maxed, setMaxed] = useState(startMaximized);
  const [, setViewport] = useState(0);
  const size = fit(want);

  useEffect(() => {
    const onResize = () => setViewport((n) => n + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* The dialog is centred, so a corner moves half of the size change: the
     size changes by twice the pointer's move to keep the corner under it. */
  const startDrag = (ev, dx, dy) => {
    ev.preventDefault();
    ev.stopPropagation();
    const x0 = ev.clientX;
    const y0 = ev.clientY;
    const start = size;
    let last = start;
    const move = (m) => {
      last = fit({ w: start.w + dx * 2 * (m.clientX - x0), h: start.h + dy * 2 * (m.clientY - y0) });
      setWant(last);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.style.userSelect = "";
      saveSize(last);
    };
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const nudge = (ev) => {
    const step = ev.shiftKey ? 80 : 20;
    const by = { ArrowRight: [step, 0], ArrowLeft: [-step, 0], ArrowDown: [0, step], ArrowUp: [0, -step] }[ev.key];
    if (!by) return;
    ev.preventDefault();
    const s = fit({ w: size.w + by[0], h: size.h + by[1] });
    setWant(s);
    saveSize(s);
  };

  const reset = () => {
    const d = defaultSize();
    setWant(d);
    saveSize(d);
  };

  return {
    maxed,
    size,
    toggleMax: () => setMaxed((m) => !m),
    startDrag,
    nudge,
    reset,
    style: maxed ? { width: `calc(100vw - ${GUTTER}px)`, height: `calc(100vh - ${GUTTER}px)` } : { width: size.w, height: size.h },
  };
}

const CORNERS = [
  { key: "sw", dx: -1, dy: 1, cls: "bottom-0 left-0 cursor-nesw-resize" },
  { key: "ne", dx: 1, dy: -1, cls: "right-0 top-0 cursor-nesw-resize" },
  { key: "nw", dx: -1, dy: -1, cls: "left-0 top-0 cursor-nwse-resize" },
];

/* Every corner drags; the bottom-right one is a visible, focusable grip. */
function ResizeHandles({ frame }) {
  return (
    <>
      {CORNERS.map((c) => (
        <span
          key={c.key}
          aria-hidden="true"
          onPointerDown={(ev) => frame.startDrag(ev, c.dx, c.dy)}
          className={`absolute z-20 h-4 w-4 touch-none ${c.cls}`}
        />
      ))}
      <button
        type="button"
        onPointerDown={(ev) => frame.startDrag(ev, 1, 1)}
        onKeyDown={frame.nudge}
        onDoubleClick={frame.reset}
        aria-label={`Resize (${frame.size.w} × ${frame.size.h}): drag, or use the arrow keys, Shift for bigger steps; double-click to reset`}
        title="Drag to resize · double-click to reset"
        className="absolute bottom-0 right-0 z-20 flex h-6 w-6 cursor-nwse-resize touch-none items-end justify-end rounded-br-2xl p-1 text-slate-400 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <Icons.Resize className="h-3 w-3" />
      </button>
    </>
  );
}

/* A small Log control that opens the panel; stops the click from
   selecting the card it sits on. */
export function ActivityButton({
  title,
  scope,
  testKey = "",
  compact = false,
  className = "",
  label = "Log",
  tooltip = "Log: who did what, and when",
  ...panel
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title={tooltip}
        aria-label={`Log of ${title}`}
        className={
          className ||
          (compact
            ? "inline-flex h-[1.875rem] w-[1.875rem] items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
            : "inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[0.6875rem] font-medium text-slate-600 shadow-sm hover:bg-slate-50")
        }
      >
        <Icons.Log className={compact || className ? "h-4 w-4" : "h-3.5 w-3.5"} />
        {!compact && label}
      </button>
      {open && <ActivityPanel title={title} scope={scope} testKey={testKey} {...panel} onClose={() => setOpen(false)} />}
    </>
  );
}
